import {
  type UtmChangeImpact,
  type UtmParam,
} from "@pages/onboarding/core/configure-platforms/platforms/constants";
import { getNorthbeamUtmPlatformConfig } from "@pages/onboarding/core/configure-platforms/platforms/metadata";

import { NorthbeamUtmPlatform } from "./configs";

// TODO: Use enum to make more concrete.
export type NorthbeamUtmParam = `nb_${string}` | "nbt";

export interface UtmPlatformParam<
  K extends NorthbeamUtmParam | UtmParam = NorthbeamUtmParam | UtmParam,
> {
  key: K;
  value: string;
}

type _UtmPlatformParam<
  K extends NorthbeamUtmParam | UtmParam = NorthbeamUtmParam | UtmParam,
> = {
  key: K;
  /* Allow the value of the parameter key-value pair to be a dynamic function for
  cases where the value depends on information that must be calculated at run
  time (e.g. dates/times). */
  value: string | (() => string);
};

type UtmPlatformConfig<
  K extends NorthbeamUtmParam | UtmParam = NorthbeamUtmParam | UtmParam,
> = {
  // If the title is not defined, the string value of the enum will be used.
  readonly title?: string;
  readonly logoUrl?: string;
  readonly changeImpact: UtmChangeImpact;
  readonly params: _UtmPlatformParam<K>[];
};

export const NORTHBEAM_UTM_PLATFORM_CONFIGS: {
  [key in NorthbeamUtmPlatform]: UtmPlatformConfig;
} = Object.values(NorthbeamUtmPlatform).reduce((acc, platformValue) => {
  const platformConfig = getNorthbeamUtmPlatformConfig(platformValue);
  acc[platformValue] = {
    title: platformConfig.title,
    logoUrl: platformConfig.logoUrl,
    changeImpact: platformConfig.utmChangeImpact,
    params: platformConfig.utmParams as _UtmPlatformParam[],
  } as UtmPlatformConfig;
  return acc;
}, {} as Record<NorthbeamUtmPlatform, UtmPlatformConfig>);

export function getNorthbeamUtmPlatformTitle({
  platform,
}: {
  platform: NorthbeamUtmPlatform;
}): string {
  const title = NORTHBEAM_UTM_PLATFORM_CONFIGS[platform].title;
  return title === undefined ? platform : title;
}

export function getNorthbeamUtmPlatformParams(options?: {
  platform?: NorthbeamUtmPlatform;
}): UtmPlatformParam[] {
  const params =
    options?.platform !== undefined
      ? NORTHBEAM_UTM_PLATFORM_CONFIGS[options.platform].params
      : Object.values(NORTHBEAM_UTM_PLATFORM_CONFIGS).flatMap(
          (config) => config.params,
        );
  return params.map(
    ({ value, key }): UtmPlatformParam =>
      typeof value === "function" ? { key, value: value() } : { key, value },
  );
}

export enum LegacyUtmPlatform {
  WICKED_REPORTS = "WickedReports",
  HYROS = "Hyros",
  ROCKERBOX = "Rockerbox",
  TRIPLE_WHALE = "Triple Whale",
}
